/**
 * Dev-only observer for the dev-dashboard.
 *
 * Streams three event types to the dashboard via WebSocket:
 * 1. Storage mutations (from generated sync-config.ts accessors)
 * 2. HTTP requests/responses (from axios interceptor)
 * 3. Socket.IO events (from socketValidation wrappers)
 *
 * Never loaded in production — Metro eliminates the __DEV__ guard.
 */

/* eslint-disable no-console -- Dev-only observer, console is intentional */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL, E2E_MODE, getDevHost } from '../config/constants';
import { STORAGE_KEYS } from '../generated/sync/sync-config';

const DASHBOARD_PORT = 3001;
// First retry fires fast (500ms). Doubled on each subsequent failure
// up to 30s. Lowered from 2000ms on 2026-06-06 so a `dev.reload`
// → JS bundle rebuild → observer.connect() race doesn't sit waiting
// the full 2s for the first reconnect attempt; CACHE-001/happy timed
// out at the dispatch's 30s ack window because the WS reconnect was
// still in the backoff queue when the next dispatch arrived at the
// relay. Engine-side waitForMobileReconnect (engine.ts) handles the
// dispatch-side race; this just makes the mobile-side recovery
// faster so the engine's poll loop exits sooner.
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 30000;
const QUEUE_MAX = 200;

type DevMsg =
  | { type: 'change'; key: string; action: 'save' | 'update' | 'clear'; oldValue: unknown; newValue: unknown; timestamp: number }
  | { type: 'read'; key: string; value: unknown; timestamp: number }
  | { type: 'http'; method: string; url: string; status: number | null; duration: number; requestBody: unknown; responseBody: unknown; error: string | null; timestamp: number }
  | { type: 'ws'; direction: 'emit' | 'receive'; event: string; data: unknown; timestamp: number }
  | { type: 'linking'; url: string; timestamp: number };

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = RECONNECT_BASE_MS;
const queue: DevMsg[] = [];
let initialized = false;

function getDeviceId(): string {
  if (Platform.OS === 'ios') return 'ios-simulator';
  if (Platform.OS === 'android') return 'android-emulator';
  return `${Platform.OS}`;
}

function send(msg: object): boolean {
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch {
      // JSON.stringify can fail on circular refs — drop the message
      return true; // treat as sent to avoid infinite queue growth
    }
  }
  return false;
}

function flushQueue() {
  while (queue.length > 0) {
    const msg = queue[0];
    if (send(msg)) {
      queue.shift();
    } else {
      break;
    }
  }
}

const ALL_DSL_VALUES = Object.values(STORAGE_KEYS) as string[];

/** All non-prefix keys defined in the sync-config DSL. */
const DSL_DEFINED_KEYS = ALL_DSL_VALUES.filter(
  (k) => !k.endsWith('_') && !k.endsWith('/'),
);

/** Prefix keys defined in the DSL (sessionEntry, freshnessDomainState). */
const DSL_PREFIXES = ALL_DSL_VALUES.filter(
  (k) => k.endsWith('_') || k.endsWith('/'),
);

/**
 * Apply a `write-storage` frame from the dashboard relay.
 *
 * `value === null` removes the key (parallel to AsyncStorage.removeItem).
 * Any other value is JSON-stringified and written via setItem. After the
 * write completes, the next snapshot request will see the new state; the
 * outbound `notifyChange` stream isn't fired here because this didn't
 * pass through the generated CoursesCacheStorage.save / etc. accessors
 * that own change emission — those wrap consumer-level writes, and this
 * is intentionally a back-door for test + dashboard tooling.
 *
 * Never crashes the app on failure — this is dev-tooling, not a normal
 * runtime path.
 */
async function handleWriteStorage(
  id: unknown,
  key: unknown,
  value: unknown,
): Promise<void> {
  if (typeof key !== 'string' || key.length === 0) {
    console.log('[storage-observer] write-storage: ignoring missing/invalid key');
    if (typeof id === 'string') {
      sendAck(id, false, 'invalid key');
    }
    return;
  }
  try {
    if (value === null) {
      await AsyncStorage.removeItem(key);
      console.log(`[storage-observer] write-storage removed key="${key}"`);
    } else {
      await AsyncStorage.setItem(key, JSON.stringify(value));
      console.log(`[storage-observer] write-storage wrote key="${key}"`);
    }
    if (typeof id === 'string') sendAck(id, true);
  } catch (e) {
    const errMsg = (e as { message?: string })?.message || 'unknown';
    console.log('[storage-observer] write-storage failed:', errMsg);
    if (typeof id === 'string') sendAck(id, false, errMsg);
  }
}

/**
 * Dispatch a named dev intent. Replaces the deep-link transport for
 * the runner's `Do(dev.*)` steps — see `BrowserInboundFrame.dev-intent`
 * in monsterrat's app-observer for the wire protocol rationale.
 *
 * The handler registry is provided by `devReset.ts` so this file stays
 * focused on transport — adding a new dev intent only needs an entry
 * in the registry, no protocol changes here.
 */
async function handleDevIntent(
  id: unknown,
  name: unknown,
  args: unknown,
): Promise<void> {
  if (typeof id !== 'string' || typeof name !== 'string') {
    return;
  }
  try {
    const { DEV_INTENT_HANDLERS } = await import('./devReset');
    const handler = DEV_INTENT_HANDLERS[name];
    if (!handler) {
      sendAck(id, false, `unknown dev intent: ${name}`);
      return;
    }
    // Handler may return data (e.g. log-storage returns a snapshot).
    // Undefined return = no payload, ack-only.
    const data = await handler(args);
    sendAck(id, true, undefined, data === undefined ? undefined : data);
  } catch (e) {
    const errMsg = (e as { message?: string })?.message || 'unknown';
    console.log(`[storage-observer] dev-intent "${name}" failed:`, errMsg);
    sendAck(id, false, errMsg);
  }
}

function sendAck(id: string, ok: boolean, error?: string, data?: unknown): void {
  const frame: { type: 'ack'; id: string; ok: boolean; error?: string; data?: unknown } = {
    type: 'ack',
    id,
    ok,
  };
  if (error !== undefined) frame.error = error;
  if (data !== undefined) frame.data = data;
  send(frame);
}

async function handleSnapshot() {
  try {
    const allKeys = await AsyncStorage.getAllKeys();
    const fluenciaKeys = allKeys.filter((k) => k.startsWith('@fluencia/'));
    const pairs = await AsyncStorage.multiGet(fluenciaKeys);

    const existingMap = new Map<string, unknown>();
    for (const [key, raw] of pairs) {
      existingMap.set(key, raw ? tryParse(raw) : null);
    }

    // Merge: DSL-defined keys (even if missing) + existing keys (even if undeclared)
    const allEntryKeys = new Set([...DSL_DEFINED_KEYS, ...existingMap.keys()]);
    const entries = Array.from(allEntryKeys).map((key) => ({
      key,
      value: existingMap.get(key) ?? null,
      exists: existingMap.has(key),
      defined: DSL_DEFINED_KEYS.includes(key) || DSL_PREFIXES.some((p) => key.startsWith(p)),
    }));

    send({ type: 'snapshot', entries, definedKeys: DSL_DEFINED_KEYS, timestamp: Date.now() });
  } catch {
    // Silently fail — dev tooling must not crash the app
  }
}

function tryParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function connect() {
  if (ws) return;

  const host = getDevHost();
  const url = `ws://${host}:${DASHBOARD_PORT}/ws/storage?role=mobile`;
  console.log(`[storage-observer] connecting to ${url}`);

  try {
    ws = new WebSocket(url);
  } catch (e) {
    console.log('[storage-observer] WebSocket constructor failed:', e);
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    console.log('[storage-observer] connected');
    reconnectDelay = RECONNECT_BASE_MS;
    // e2eMode + apiUrl let the E2E runner verify, at run start, that the
    // app's JS bundle was served by a Metro launched with E2E_MODE=true
    // (and API_URL pointing at the traffic proxy). Both come from the
    // manifest of whichever Metro the dev client loaded from — a stale
    // dev Metro on port 8081 silently yields e2eMode=false, which
    // disables LogBox toast suppression, the dev-menu FAB hiding, and
    // the boot barrier. The runner aborts with APP_ENV_MISMATCH instead
    // of running flaky tests against that app.
    send({
      type: 'register',
      deviceId: getDeviceId(),
      platform: Platform.OS,
      e2eMode: E2E_MODE,
      apiUrl: API_URL,
    });
    flushQueue();
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(typeof event.data === 'string' ? event.data : '');
      if (msg.type === 'request-snapshot') {
        handleSnapshot();
        return;
      }
      if (msg.type === 'write-storage') {
        handleWriteStorage(msg.id, msg.key, msg.value);
        return;
      }
      if (msg.type === 'dev-intent') {
        handleDevIntent(msg.id, msg.name, msg.args);
        return;
      }
    } catch {
      // Ignore malformed messages
    }
  };

  ws.onclose = () => {
    console.log('[storage-observer] disconnected, will reconnect');
    ws = null;
    scheduleReconnect();
  };

  ws.onerror = (e) => {
    console.log('[storage-observer] error:', (e as { message?: string })?.message || 'unknown');
    // onclose will fire after onerror — reconnect handled there
    try { ws?.close(); } catch { /* ignore */ }
  };
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
    connect();
  }, reconnectDelay);
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Initialize the storage observer.
 * Call once from App.tsx behind a __DEV__ guard.
 */
export function initStorageObserver(): void {
  if (initialized) return;
  initialized = true;
  console.log('[storage-observer] initializing');
  connect();
}

/**
 * Notify about a storage change.
 * Called by generated accessors in sync-config.ts.
 * Lazily initializes the connection if not already started.
 */
export function notifyChange(
  key: string,
  action: 'save' | 'update' | 'clear',
  oldValue: unknown,
  newValue: unknown,
): void {
  enqueue({ type: 'change', key, action, oldValue, newValue, timestamp: Date.now() });
}

/**
 * Notify about a storage read (every `Storage.load()` invocation).
 * Called by generated accessors in sync-config.ts. Lazily initializes
 * the connection if not already started.
 *
 * Reads are MUCH higher-frequency than writes — every `isFresh` check,
 * every screen mount that reads a cached value, every freshness probe.
 * The E2E runner records them into the unified call log so the
 * dashboard's run-detail view shows the full AsyncStorage activity
 * timeline. Production builds strip the entire codegen branch (via
 * `__DEV__ && _notifyRead` guards), so there's no production cost.
 */
export function notifyRead(key: string, value: unknown): void {
  enqueue({ type: 'read', key, value, timestamp: Date.now() });
}

/**
 * Notify about an HTTP request/response.
 * Called by the dev-only axios interceptor installed in apiClient.ts.
 */
export function notifyHttp(
  method: string,
  url: string,
  status: number | null,
  duration: number,
  requestBody: unknown,
  responseBody: unknown,
  error: string | null,
): void {
  enqueue({ type: 'http', method, url, status, duration, requestBody, responseBody, error, timestamp: Date.now() });
}

/**
 * Notify about a Socket.IO event.
 * Called by the dev-only wrappers in socketValidation.ts.
 */
export function notifyWs(
  direction: 'emit' | 'receive',
  event: string,
  data: unknown,
): void {
  enqueue({ type: 'ws', direction, event, data, timestamp: Date.now() });
}

/**
 * Notify about a (would-be) external-navigation attempt — `Linking.openURL`
 * or any other platform handoff that takes the user out of the app.
 *
 * Called by `mobile/src/utils/linking.ts` instead of the platform call
 * itself when `E2E_MODE` is on. The actual platform open is suppressed
 * in E2E (otherwise Safari/the store app would background Fluencia and
 * every subsequent UI step would fail). The runner records the URL in
 * the unified call log under the `external-nav` surface so stories can
 * assert it via `ExpectLink(url)`.
 *
 * Outside of E2E this is never called — production paths invoke
 * `Linking.openURL` directly through the wrapper without going through
 * the observer.
 */
export function notifyLinking(url: string): void {
  enqueue({ type: 'linking', url, timestamp: Date.now() });
}

function enqueue(msg: DevMsg): void {
  if (!initialized) {
    initialized = true;
    connect();
  }

  if (!send(msg)) {
    queue.push(msg);
    if (queue.length > QUEUE_MAX) {
      queue.shift();
    }
  }
}
